<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { setResponseHeader } from "h3";
import AppHeader from "~/components/header/AppHeader.vue";
import CardGrid from "~/components/card-grid/CardGrid.vue";
import EstablishmentCard from "~/components/cards/EstablishmentCard.vue";
import type { UiEstablishmentCard } from "~~/shared/models/cards";

const PAGE_SIZE = 24;

type DiscoveryResponse = {
  establishments: UiEstablishmentCard[];
  page: number;
  limit: number;
  total: number;
  sourceCount: number;
  hasMore: boolean;
};

function parsePage(value: unknown): number {
  if (value === undefined) return 1;
  if (Array.isArray(value) || !/^[1-9]\d*$/.test(String(value))) {
    throw createError({
      statusCode: 404,
      statusMessage: "Venues page not found",
      fatal: true,
    });
  }

  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed)) {
    throw createError({
      statusCode: 404,
      statusMessage: "Venues page not found",
      fatal: true,
    });
  }
  return parsed;
}

const route = useRoute();
const localePath = useLocalePath();
const { locale, t } = useI18n();
const { APP_URL } = usePublicUrls();
const { toAbsolutePublicUrl } = useSocialSeo();
const page = computed(() => parsePage(route.query.page));
const mode = ref<"event" | "establishment">("establishment");

const { data, error } = await useAsyncData(
  computed(() => `public-venues-${page.value}`),
  () =>
    $fetch<DiscoveryResponse>("/bff/establishments", {
      query: { page: page.value, limit: PAGE_SIZE },
    }),
);

if (error.value) {
  if (import.meta.server) {
    const requestEvent = useRequestEvent();
    if (requestEvent)
      setResponseHeader(requestEvent, "Cache-Control", "no-store");
  }
  throw createError({
    statusCode: 503,
    statusMessage: "Public venues unavailable",
    fatal: true,
    cause: error.value,
  });
}
if (page.value > 1 && (data.value?.sourceCount ?? 0) === 0) {
  throw createError({
    statusCode: 404,
    statusMessage: "Venues page not found",
    fatal: true,
  });
}

watch([page, data, error], ([currentPage, response, fetchError]) => {
  if (fetchError) {
    showError(fetchError);
    return;
  }
  if (response && currentPage > 1 && response.sourceCount === 0) {
    showError({
      statusCode: 404,
      statusMessage: "Venues page not found",
      fatal: true,
    });
  }
});

const venues = computed(() => data.value?.establishments ?? []);
const hasMore = computed(() => Boolean(data.value?.hasMore));

function pagePath(targetPage: number, targetLocale = locale.value): string {
  const path = localePath("/venues", targetLocale);
  return targetPage === 1 ? path : `${path}?page=${targetPage}`;
}

const previousPath = computed(() =>
  page.value > 1 ? pagePath(page.value - 1) : null,
);
const nextPath = computed(() =>
  hasMore.value ? pagePath(page.value + 1) : null,
);
const canonicalUrl = computed(() => toAbsolutePublicUrl(pagePath(page.value)));
const alternateEnUrl = computed(() =>
  toAbsolutePublicUrl(pagePath(page.value, "en")),
);
const alternateDeUrl = computed(() =>
  toAbsolutePublicUrl(pagePath(page.value, "de")),
);
const pageTitle = computed(() =>
  page.value === 1
    ? t("venues.title")
    : t("venues.pageTitle", { page: page.value }),
);

useSeoMeta({
  title: () => `${pageTitle.value} | Upfest`,
  description: () => t("venues.description"),
  ogTitle: pageTitle,
  ogDescription: () => t("venues.description"),
  ogUrl: canonicalUrl,
  ogType: "website",
  ogSiteName: "Upfest",
  twitterCard: "summary_large_image",
  twitterTitle: pageTitle,
  twitterDescription: () => t("venues.description"),
});

useHead(
  computed(() => ({
    link: [
      { rel: "canonical", href: canonicalUrl.value },
      { rel: "alternate", hreflang: "en", href: alternateEnUrl.value },
      { rel: "alternate", hreflang: "de", href: alternateDeUrl.value },
      { rel: "alternate", hreflang: "x-default", href: alternateEnUrl.value },
      ...(previousPath.value
        ? [{ rel: "prev", href: toAbsolutePublicUrl(previousPath.value) }]
        : []),
      ...(nextPath.value
        ? [{ rel: "next", href: toAbsolutePublicUrl(nextPath.value) }]
        : []),
    ],
  })),
);

async function onModeChange(next: "event" | "establishment") {
  if (next === "event") {
    await navigateTo(localePath("/events"));
    return;
  }
  mode.value = next;
}

function onHeaderLogin() {
  if (import.meta.client) window.location.href = `${APP_URL}/`;
}
</script>

<template>
  <NuxtLayout>
    <div class="min-h-screen bg-white text-purple-950">
      <AppHeader
        :mode="mode"
        @update:mode="onModeChange"
        @login="onHeaderLogin"
      />

      <div
        class="mx-auto w-full max-w-[1366px] px-4 pb-16 pt-10 sm:px-6 lg:px-12"
      >
        <header class="max-w-3xl border-b border-purple-100 pb-7">
          <p
            class="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-pink-500"
          >
            {{ $t("venues.eyebrow") }}
          </p>
          <h1
            class="font-rounded text-3xl font-extrabold tracking-tight md:text-5xl"
          >
            {{ pageTitle }}
          </h1>
          <p class="mt-4 max-w-2xl text-base leading-7 text-slate-600">
            {{ $t("venues.description") }}
          </p>
        </header>

        <CardGrid
          class="mt-8"
          title=""
          :items="venues"
          :item-component="EstablishmentCard"
          :has-more="false"
          :loading="false"
          :single-column-on-mobile="true"
        />

        <div
          v-if="venues.length === 0"
          class="py-16 text-center text-slate-500"
        >
          {{ $t("venues.empty") }}
        </div>

        <nav
          class="mt-12 flex items-center justify-between border-t border-purple-100 pt-6"
          :aria-label="$t('venues.pagination')"
        >
          <NuxtLink
            v-if="previousPath"
            :to="previousPath"
            rel="prev"
            class="rounded-full border border-purple-200 px-5 py-2.5 text-sm font-bold transition-colors hover:bg-purple-50"
          >
            {{ $t("venues.previous") }}
          </NuxtLink>
          <span v-else></span>
          <span class="text-sm font-semibold text-slate-500">{{
            $t("venues.page", { page })
          }}</span>
          <NuxtLink
            v-if="nextPath"
            :to="nextPath"
            rel="next"
            class="rounded-full bg-purple-950 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-purple-800"
          >
            {{ $t("venues.next") }}
          </NuxtLink>
          <span v-else></span>
        </nav>
      </div>
    </div>
  </NuxtLayout>
</template>
